// NodeMultiselectModal.jsx

import React, { useEffect, useMemo, useState } from 'react';

// Context
import { useCurrentLanguage } from 'context/LanguageContext';
import { useTheme } from 'context/ThemeContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import { getOptionLabel, resolveEnumOptionsForField } from 'utilities/EnumOptions';
import { getResolvedFields } from 'utilities/NodeType';

// CSS
import './NodeMultiselectModal.css';

// Icons
import Icon from 'svg/Icon';

// Components
import Button from 'components/button/Button';
import Checkbox from 'components/checkbox/Checkbox';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalForm from 'components/modal/form/ModalForm';
import Modal from 'components/modal/Modal';

function resolveMultiselectField(node, field, fieldName) {
    if (field?.type === 'multiselect') return field;

    const fields = getResolvedFields(node?.type, { includeHidden: true, excludeSecured: true });
    return fields.find((entry) => entry.name === fieldName && entry.type === 'multiselect') || null;
}

const normalizeSelectedValues = (value) => {
    const list = Array.isArray(value) ? value : value == null || value === '' ? [] : [value];
    const seen = new Set();

    return list
        .map((entry) => (typeof entry === 'string' ? entry.trim() : String(entry || '').trim()))
        .filter((entry) => {
            if (!entry || seen.has(entry)) return false;
            seen.add(entry);
            return true;
        });
};

const orderSelectedValues = (options, selectedValues) => {
    const selectedSet = new Set(normalizeSelectedValues(selectedValues));
    const ordered = [];

    (Array.isArray(options) ? options : []).forEach((option) => {
        const optionValue = String(option?.value || '').trim();
        if (!optionValue || !selectedSet.has(optionValue)) return;
        ordered.push(optionValue);
        selectedSet.delete(optionValue);
    });

    return [...ordered, ...Array.from(selectedSet)];
};

/**
 * NodeMultiselectModal
 *
 * Allows choosing one or more enum-like options for a multiselect field.
 *
 * @param {object} node - Node object used to resolve field configuration.
 * @param {object} field - Optional field definition for direct option resolution.
 * @param {string} fieldName - Name of the schema field.
 * @param {string} label - Display label for the modal title.
 * @param {string[]|string} value - Current selected option values.
 * @param {boolean} isOpen - Whether the modal is open.
 * @param {function} setOpen - Setter for modal open state.
 * @param {function} onChange - Callback when selections are saved.
 *
 * @returns {ReactNode} The rendered modal.
 */
const NodeMultiselectModal = ({ node, field, fieldName, label, value, isOpen, setOpen, onChange }) => {

    // ----- DEVICE DETECTION ----- //

    const isMobile = useIsMobile();
    // ----- CONTEXT ----- //

    const language = useCurrentLanguage();
    const { theme } = useTheme();

    // ----- STATE ----- //

    const [selectedValues, setSelectedValues] = useState([]);

    // ----- MEMOS ----- //

    const options = useMemo(() => {
        const fieldDef = resolveMultiselectField(node, field, fieldName);
        return resolveEnumOptionsForField(fieldDef, language);
    }, [field, fieldName, language, node]);

    const orderedSelectedValues = useMemo(() => orderSelectedValues(options, selectedValues), [options, selectedValues]);

    // ----- EFFECTS ----- //

    useEffect(() => {
        if (!isOpen) return;
        setSelectedValues(normalizeSelectedValues(value));
    }, [isOpen, value]);

    // ----- HANDLERS ----- //

    /**
 * Handles toggle.
 *
 * @param {*} optionValue
 */

    const handleToggle = (optionValue) => {
        const nextValue = String(optionValue || '').trim();
        if (!nextValue) return;

        setSelectedValues((prev) => {
            const current = normalizeSelectedValues(prev);
            return current.includes(nextValue)
                ? current.filter((entry) => entry !== nextValue)
                : [...current, nextValue];
        });
    };

    /**
 * Handles save.
 */
    const handleSave = () => {
        onChange?.(orderedSelectedValues);
        setOpen(false);
    };

    // ----- RENDER ----- //

    return (
        <Modal
          title={label || 'Multi-Select'}
          isOpen={isOpen}
          width={isMobile ? '100%' : '420px'}
          height="auto"
          onClose={() => setOpen(false)}
        >
            <ModalForm>
            <div className="node-multiselect-modal-container">

                <div className="node-multiselect-modal-actions">
                    <Button
                      label="Select All"
                      enabled={options.length > 0}
                      height="34px"
                      onClick={() => setSelectedValues(options.map((option) => String(option?.value || '').trim()).filter(Boolean))}
                    />
                    <Button
                      label="Clear"
                      enabled={orderedSelectedValues.length > 0}
                      height="34px"
                      onClick={() => setSelectedValues([])}
                    />
                </div>

                {/* LIST */}
                <div
                  className="node-multiselect-modal-list"
                >
                    {options.length === 0 ? (
                        <div
                          className="node-multiselect-modal-empty"
                        >
                            No options configured.
                        </div>
                    ) : (
                        options.map((option) => {
                            const optionValue = String(option?.value || '').trim();
                            const checked = orderedSelectedValues.includes(optionValue);

                            return (
                                <div
                                  className="node-multiselect-modal-row"
                                  style={{
                                    backgroundColor: checked ? theme.backgroundMuted : 'transparent',
                                  }}
                                  key={optionValue}
                                >
                                    <Checkbox
                                      value={checked}
                                      size={28}
                                      onChange={() => handleToggle(optionValue)}
                                    />

                                    {/* ROW BUTTON */}
                                    <button
                                      className="node-multiselect-modal-row-button"
                                      type="button"
                                      onClick={() => handleToggle(optionValue)}
                                    >

                                        {/* ROW LABEL WRAP */}
                                        <span className="node-multiselect-modal-row-label-wrap">
                                            {option?.iconKey ? (
                                                <Icon
                                                  iconKey={option.iconKey}
                                                  width={16}
                                                  height={16}
                                                  color={theme.foregroundMuted}
                                                />
                                            ) : null}

                                            {/* ROW LABEL */}
                                            <span className="node-multiselect-modal-row-label">
                                                {getOptionLabel(option, language)}
                                            </span>
                                        </span>

                                        {/* ROW DOT */}
                                        {option?.color ? (
                                            <span
                                              className="node-multiselect-modal-row-dot"
                                              style={{
                                                    backgroundColor: option.color,
                                                }}
                                            />
                                        ) : null}
                                    </button>
                                </div>
                            );
                        })
                    )}
                </div>

                {/* SUMMARY */}
                <div
                  className="node-multiselect-modal-summary"
                >
                    {orderedSelectedValues.length} selected
                </div>
            </div>
            </ModalForm>

            <ModalButtons>
                <Button
                  label="Cancel"
                  onClick={() => setOpen(false)}
                />
                <Button
                  iconKey="check"
                  label="Save"
                  onClick={handleSave}
                />
            </ModalButtons>
        </Modal>
    );
};

// Dev Tools
NodeMultiselectModal.displayName = 'NodeMultiselectModal';

export default NodeMultiselectModal;
